import dayjs from "dayjs";
import db from "../db/database";
import { getCurrentDateTimeISO } from "../utils/dateUtils";
import { markForSync } from "./syncService";

// Función para obtener el siguiente número de factura
export const getNextInvoiceNumber = async () => {
	try {
		// Obtener todas las facturas para encontrar el número más alto
		const allInvoices = await db.invoices.toArray();

		if (allInvoices.length === 0) {
			// Si no hay facturas, empezar desde F00001
			return "F00001";
		}

		// Filtrar facturas con formato válido (que empiecen con F seguido de números)
		const validNumbers = allInvoices
			.map((invoice) => invoice.No)
			.filter((no) => no && /^F\d+$/.test(no))
			.map((no) => parseInt(no.substring(1), 10));

		if (validNumbers.length === 0) {
			return "F00001";
		}

		// Encontrar el número máximo y agregar 1
		const maxNumber = Math.max(...validNumbers);
		const nextNumber = maxNumber + 1;

		// Formatear el número con ceros a la izquierda (F00001, F00002, etc.)
		return `F${nextNumber.toString().padStart(5, "0")}`;
	} catch (error) {
		console.error("Error al generar el siguiente número de factura:", error);
		// En caso de error, generar un número basado en la fecha y hora actual
		const timestamp = Date.now().toString().slice(-6);
		return `F${timestamp}`;
	}
};

// Funciones para gestionar facturas
export const addInvoice = async (invoice) => {
	try {
		// Calcular el descuento máximo de formas de pago si no viene en la factura
		let dto = invoice.Dto || 0;
		let importeDto = invoice.importeDto || 0;

		// Crear una factura en la base de datos local
		const id = await db.invoices.add({
			Document_Type: invoice.Document_Type || "Invoice",
			Posting_Date: invoice.Posting_Date,
			Document_Date: invoice.Document_Date,
			Sell_to_Customer_No_: invoice.Sell_to_Customer_No_,
			Sell_to_Customer_Name_2: invoice.Sell_to_Customer_Name_2 || "",
			Razon_Social: invoice.Razon_Social || "",
			Salesperson_Code: invoice.Salesperson_Code || "",
			// Campo para guardar el ID del cliente cuando aún no tiene No
			clientLocalId:
				typeof invoice.Sell_to_Customer_No_ === "number"
					? invoice.Sell_to_Customer_No_
					: null,
			// Datos de facturación
			Bill_to_Name: invoice.Bill_to_Name,
			Bill_to_Address: invoice.Bill_to_Address,
			Bill_to_City: invoice.Bill_to_City || "",
			VAT_Registration_No_: invoice.VAT_Registration_No_ || "",
			Bill_to_Country_Region_Code: invoice.Bill_to_Country_Region_Code || "",
			// Código de dirección de envío
			Ship_to_Code: invoice.Ship_to_Code || "",
			// Tipo de documento de identificación fiscal
			RTN: invoice.RTN,
			Tipo_Documento_SrI: invoice.Tipo_Documento_SrI || "Cedula",
			Fiscal_Invoice_Type: invoice.Fiscal_Invoice_Type || "Factura consumidor final",
			NCR: invoice.NCR || "",
			Colegio: invoice.Colegio || null,
			// Datos para abonos
			Applies_to_Doc_Type: invoice.Applies_to_Doc_Type,
			Applies_to_Doc_No: invoice.Applies_to_Doc_No,
			// Datos de caja y turno
			caja: invoice.caja || null,
			turno: invoice.turno || null,
			// Campos de descuento
			Dto: dto,
			importeDto: importeDto,
			// Campos de cupón
			Cupon: invoice.Cupon || null,
			Tipo_Detalle: invoice.Tipo_Detalle || null,
			No_Detalle: invoice.No_Detalle || null,
			// Campo de campaña
			Campaign_No_: invoice.Campaign_No_ || null,
			// Estado de borrador (por defecto no es borrador)
			isDraft: invoice.isDraft || false,
			Pre_Assigned_No: invoice.Document_Type === "Invoice" ? invoice.Pre_Assigned_No : "",
			pendingSync: 1,
			lastModified: new Date(),
			deleted: false,
		});

		return { ...invoice, id };
	} catch (error) {
		console.error("Error al crear factura:", error);
		throw error;
	}
};

// Función para guardar una factura como borrador
export const saveInvoiceAsDraft = async (invoice) => {
	try {
		// Asegurarnos de que se guarda como borrador
		const draftInvoice = {
			...invoice,
			isDraft: true,
		};

		// Utilizar la función addInvoice existente con la marca de borrador
		return await addInvoice(draftInvoice);
	} catch (error) {
		console.error("Error al guardar factura como borrador:", error);
		throw error;
	}
};

// Función para convertir un borrador en factura normal
export const convertDraftToInvoice = async (id) => {
	try {
		// Verificar que la factura existe
		const invoice = await getInvoiceById(id);
		if (!invoice || invoice.deleted) {
			throw new Error("Factura no encontrada");
		}

		// Verificar que la factura es un borrador
		if (!invoice.isDraft) {
			throw new Error("La factura no es un borrador");
		}

		// Actualizar el estado de borrador
		await db.invoices.update(id, {
			isDraft: false,
			lastModified: new Date(),
			pendingSync: 1,
		});

		// Marcar como pendiente de sincronización
		await markForSync("invoices", id);

		return await getInvoiceById(id);
	} catch (error) {
		console.error(`Error al convertir borrador a factura con ID ${id}:`, error);
		throw error;
	}
};

export const getAllInvoices = async () => {
	try {
		return await db.invoices.filter((invoice) => !invoice.deleted).toArray();
	} catch (error) {
		console.error("Error al obtener facturas:", error);
		throw error;
	}
};

// Función para obtener todos los borradores
export const getAllDrafts = async () => {
	try {
		return await db.invoices
			.filter((invoice) => !invoice.deleted && invoice.isDraft === true)
			.toArray();
	} catch (error) {
		console.error("Error al obtener borradores:", error);
		throw error;
	}
};

// Función para obtener todas las facturas que no son borradores
export const getAllNonDraftInvoices = async () => {
	try {
		return await db.invoices
			.filter((invoice) => !invoice.deleted && !invoice.isDraft)
			.toArray();
	} catch (error) {
		console.error("Error al obtener facturas no borrador:", error);
		throw error;
	}
};

export const getInvoiceById = async (id) => {
	try {
		// Convertir ID a número si es necesario
		const numericId = parseInt(id, 10);
		if (isNaN(numericId)) {
			throw new Error("ID de factura no es un número válido");
		}

		return await db.invoices.get(numericId);
	} catch (error) {
		console.error(`Error al obtener factura con ID ${id}:`, error);
		throw error;
	}
};

export const getInvoiceByNo = async (No, fieldName = "No") => {
	try {
		return await db.invoices
			.where(fieldName)
			.equals(No)
			.and((invoice) => !invoice.deleted)
			.first();
	} catch (error) {
		console.error(`Error al obtener factura con No ${No}:`, error);
		throw error;
	}
};

export const updateInvoice = async (id, invoiceData) => {
	try {
		const invoice = await db.invoices.get(id);
		if (!invoice || invoice.deleted) {
			throw new Error("Factura no encontrada");
		}

		if (invoice.No) {
			throw new Error(
				"No se puede modificar una factura que ya ha sido sincronizada con Business Central"
			);
		}

		// Verificar si Sell_to_Customer_No_ es un ID local o un No
		// Preservar clientLocalId existente salvo que nos envíen explícitamente un número
		let clientLocalId = invoice.clientLocalId || null;
		if (
			invoiceData.Sell_to_Customer_No_ !== undefined &&
			typeof invoiceData.Sell_to_Customer_No_ === "number"
		) {
			clientLocalId = invoiceData.Sell_to_Customer_No_;
		}

		// Asegurarnos de que los campos caja y turno se incluyen en la actualización
		const updatedData = {
			...invoiceData,
			clientLocalId,
			// Asegurar que los campos de descuento están incluidos
			Dto: invoiceData.Dto !== undefined ? invoiceData.Dto : invoice.Dto,
			importeDto:
				invoiceData.importeDto !== undefined
					? invoiceData.importeDto
					: invoice.importeDto,
			// Asegurar que el campo cupón está incluido
			Cupon:
				invoiceData.Cupon !== undefined ? invoiceData.Cupon : invoice.Cupon,
			Tipo_Detalle:
				invoiceData.Tipo_Detalle !== undefined
					? invoiceData.Tipo_Detalle
					: invoice.Tipo_Detalle,
			No_Detalle:
				invoiceData.No_Detalle !== undefined
					? invoiceData.No_Detalle
					: invoice.No_Detalle,
			// Asegurar que el campo de campaña está incluido
			Campaign_No_:
				invoiceData.Campaign_No_ !== undefined
					? invoiceData.Campaign_No_
					: invoice.Campaign_No_,
			// Asegurar que el campo de tipo de documento está incluido
			Tipo_Documento_SrI:
				invoiceData.Tipo_Documento_SrI !== undefined
					? invoiceData.Tipo_Documento_SrI
					: invoice.Tipo_Documento_SrI || "Cedula",
			Fiscal_Invoice_Type:
				invoiceData.Fiscal_Invoice_Type !== undefined
					? invoiceData.Fiscal_Invoice_Type
					: invoice.Fiscal_Invoice_Type || "Factura consumidor final",
			NCR:
				invoiceData.NCR !== undefined
					? invoiceData.NCR
					: invoice.NCR || "",
			lastModified: new Date(),
			pendingSync: 1,
		};

		// Actualizar la factura
		await db.invoices.update(id, updatedData);

		// Marcar como pendiente de sincronización
		await markForSync("invoices", id);

		return await getInvoiceById(id);
	} catch (error) {
		console.error(`Error al actualizar factura con ID ${id}:`, error);
		throw error;
	}
};

export const deleteInvoice = async (id) => {
	try {
		// Verificar que el ID sea válido
		if (!id && id !== 0) {
			throw new Error("ID de factura inválido");
		}

		// Convertir ID a número si es necesario
		const numericId = parseInt(id, 10);
		if (isNaN(numericId)) {
			throw new Error("ID de factura no es un número válido");
		}

		// Verificar primero si la factura existe y no está sincronizada
		const existingInvoice = await getInvoiceById(numericId);

		if (!existingInvoice) {
			throw new Error("La factura no existe");
		}

		// No permitir eliminar si ya tiene número de BC
		if (existingInvoice.No) {
			throw new Error(
				"No se puede eliminar una factura que ya ha sido sincronizada con Business Central"
			);
		}

		const now = getCurrentDateTimeISO();

		// Marcar como eliminado (soft delete)
		const updateCount = await db.invoices.update(numericId, {
			deleted: true,
			pendingSync: 1,
			lastModified: now,
		});

		if (updateCount === 0) {
			throw new Error("No se pudo marcar la factura como eliminada");
		}

		// También marcar las líneas asociadas como eliminadas
		const invoiceLines = await db.invoiceLines
			.where("invoiceId")
			.equals(numericId)
			.toArray();

		for (const line of invoiceLines) {
			await db.invoiceLines.update(line.id, {
				deleted: true,
				pendingSync: 1,
				lastModified: now,
			});
		}

		return true;
	} catch (error) {
		console.error(`Error al eliminar factura con ID ${id}:`, error);
		throw error;
	}
};

// Funciones adicionales para trabajar con las líneas de factura

export const addInvoiceLine = async (invoiceLine) => {
	try {
		// Verificar primero si la factura existe y no está sincronizada
		const parentInvoice = await getInvoiceById(invoiceLine.invoiceId);
		if (!parentInvoice) {
			throw new Error("La factura no existe");
		}

		// No permitir añadir líneas si la factura ya está sincronizada
		if (parentInvoice.No) {
			throw new Error(
				"No se pueden añadir líneas a una factura que ya ha sido sincronizada con Business Central"
			);
		}

		const now = getCurrentDateTimeISO();
		const lineWithMeta = {
			...invoiceLine,
			pendingSync: 1,
			lastModified: now,
			lastSynced: null,
			deleted: false,
		};

		const id = await db.invoiceLines.add(lineWithMeta);
		return { ...lineWithMeta, id };
	} catch (error) {
		console.error("Error al agregar línea de factura:", error);
		throw error;
	}
};

export const getInvoiceLines = async (invoiceId) => {
	try {
		return await db.invoiceLines
			.where("invoiceId")
			.equals(invoiceId)
			.and((line) => !line.deleted)
			.toArray();
	} catch (error) {
		console.error(
			`Error al obtener líneas de factura para ID ${invoiceId}:`,
			error
		);
		throw error;
	}
};

export const updateInvoiceLine = async (id, updatedLine) => {
	try {
		// Obtener la línea actual
		const existingLine = await db.invoiceLines.get(id);
		if (!existingLine) {
			throw new Error("La línea de factura no existe");
		}

		// Verificar si la factura padre está sincronizada
		const parentInvoice = await getInvoiceById(existingLine.invoiceId);
		if (!parentInvoice) {
			throw new Error("La factura no existe");
		}

		// No permitir actualizar si la factura ya está sincronizada
		if (parentInvoice.No) {
			throw new Error(
				"No se pueden modificar líneas de una factura que ya ha sido sincronizada con Business Central"
			);
		}

		const now = getCurrentDateTimeISO();

		const lineToUpdate = {
			...updatedLine,
			pendingSync: 1,
			lastModified: now,
		};

		await db.invoiceLines.update(id, lineToUpdate);
		return { ...lineToUpdate, id };
	} catch (error) {
		console.error(`Error al actualizar línea de factura con ID ${id}:`, error);
		throw error;
	}
};

export const deleteInvoiceLine = async (id) => {
	try {
		// Obtener la línea actual
		const existingLine = await db.invoiceLines.get(id);
		if (!existingLine) {
			throw new Error("La línea de factura no existe");
		}

		// Verificar si la factura padre está sincronizada
		const parentInvoice = await getInvoiceById(existingLine.invoiceId);
		if (!parentInvoice) {
			throw new Error("La factura no existe");
		}

		// No permitir eliminar si la factura ya está sincronizada
		if (parentInvoice.No) {
			throw new Error(
				"No se pueden eliminar líneas de una factura que ya ha sido sincronizada con Business Central"
			);
		}

		const now = getCurrentDateTimeISO();

		await db.invoiceLines.update(id, {
			deleted: true,
			pendingSync: 1,
			lastModified: now,
		});

		return true;
	} catch (error) {
		console.error(`Error al eliminar línea de factura con ID ${id}:`, error);
		throw error;
	}
};

// Función para obtener factura completa con sus líneas
export const getCompleteInvoice = async (invoiceId) => {
	try {
		const invoice = await getInvoiceById(invoiceId);
		if (!invoice) return null;

		let lines = [];
		// priorizar No (document number final en BC); fallback a Pre_Assigned_No si aún no está contabilizado
		const invoiceNoToUse = invoice.No || invoice.Pre_Assigned_No;

		if (invoiceNoToUse) {

			lines = await db.invoiceLines
				.where("invoiceNo")
				.equals(invoiceNoToUse)
				.and(l => !l.deleted)
				.toArray();
		}
		if (lines.length == 0) {
			// local invoice sin sincronizar → usa autoincremental Dexie id
			lines = await db.invoiceLines
				.where("invoiceId")
				.equals(invoice.id)
				.and(l => !l.deleted)
				.toArray();
		}

		return { ...invoice, lines };
	} catch (error) {
		console.error(
			`Error al obtener factura completa con ID ${invoiceId}:`,
			error
		);
		throw error;
	}
};

// Función para obtener las facturas del día actual
export const getTodaysInvoices = async () => {
	try {
		const today = dayjs().format("YYYY-MM-DD");

		return await db.invoices
			.filter((invoice) => {
				// Verificar que no esté eliminada
				if (invoice.deleted) return false;

				// Verificar la fecha usando Posting_Date
				if (!invoice.Posting_Date) return false;

				const postingDateFormatted = dayjs(invoice.Posting_Date).format(
					"YYYY-MM-DD"
				);

				// Solo incluir facturas de hoy
				return postingDateFormatted === today;
			})
			.toArray();
	} catch (error) {
		console.error("Error al obtener facturas del día:", error);
		throw error;
	}
};

// Función para obtener facturas en un rango de fecha y hora específico
// (útil para cierres de caja con múltiples turnos)
export const getInvoicesByDateTimeRange = async (
	startDateTime,
	endDateTime = null
) => {
	try {
		// Si no se proporciona fecha de fin, usar la fecha y hora actual
		const endDate = endDateTime || new Date();

		// Convertir fechas a objetos dayjs para comparación
		const startDate = dayjs(startDateTime);
		const endDateFormatted = dayjs(endDate);

		console.log(
			`🔍 Buscando facturas desde: ${startDate.format(
				"YYYY-MM-DD HH:mm:ss"
			)} hasta: ${endDateFormatted.format("YYYY-MM-DD HH:mm:ss")}`
		);

		return await db.invoices
			.filter((invoice) => {
				// Verificar que no esté eliminada ni sea borrador
				if (invoice.deleted || invoice.isDraft) return false;

				// Usar lastModified para determinar cuándo se creó/modificó la factura
				if (!invoice.lastModified) return false;

				const invoiceDateTime = dayjs(invoice.lastModified);

				// La factura debe estar dentro del rango de tiempo
				const isInRange =
					invoiceDateTime.isAfter(startDate) &&
					invoiceDateTime.isBefore(endDateFormatted);

				if (isInRange) {
					console.log(
						`✅ Factura incluida: ${invoice.No || invoice.id
						} - ${invoiceDateTime.format("YYYY-MM-DD HH:mm:ss")}`
					);
				}

				return isInRange;
			})
			.toArray();
	} catch (error) {
		console.error(
			"Error al obtener facturas por rango de fecha y hora:",
			error
		);
		throw error;
	}
};

// Función para obtener facturas por tipo
export const getInvoicesByType = async (documentType) => {
	try {
		return await db.invoices
			.filter(
				(invoice) => !invoice.deleted && invoice.Document_Type === documentType
			)
			.toArray();
	} catch (error) {
		console.error(`Error al obtener facturas de tipo ${documentType}:`, error);
		throw error;
	}
};

// Función para obtener todos los abonos relacionados con una factura específica
export const getCreditMemosForInvoice = async (invoiceNo) => {
	try {
		return await db.invoices
			.filter(
				(invoice) =>
					!invoice.deleted &&
					invoice.Document_Type === "Credit Memo" &&
					invoice.Applies_to_Doc_No === invoiceNo
			)
			.toArray();
	} catch (error) {
		console.error(
			`Error al obtener las notas de crédito para la factura ${invoiceNo}:`,
			error
		);
		throw error;
	}
};

// Función para obtener facturas del día por tipo
export const getTodaysInvoicesByType = async (documentType) => {
	try {
		const todaysInvoices = await getTodaysInvoices();
		return todaysInvoices.filter(
			(invoice) => invoice.Document_Type === documentType
		);
	} catch (error) {
		console.error(
			`Error al obtener facturas de hoy de tipo ${documentType}:`,
			error
		);
		throw error;
	}
};

// Función para obtener facturas pendientes de sincronizar del día de hoy
export const getTodaysPendingInvoices = async () => {
	try {
		const todaysInvoices = await getTodaysInvoices();
		return todaysInvoices.filter(
			(invoice) => !invoice.No && !invoice.deleted && !invoice.isDraft
		);
	} catch (error) {
		console.error("Error al obtener facturas pendientes de hoy:", error);
		throw error;
	}
};

// Función para actualizar el No de cliente en las facturas cuando el cliente se sincroniza
export const updateClientNoInInvoices = async (clientId, clientNo) => {
	try {
		// Buscar de forma robusta: por clientLocalId, por Sell_to_Customer_No_ num y string
		const byLocalId = await db.invoices
			.where("clientLocalId")
			.equals(clientId)
			.toArray();

		const bySellToNum = await db.invoices
			.where("Sell_to_Customer_No_")
			.equals(clientId)
			.toArray();

		const bySellToStr = await db.invoices
			.where("Sell_to_Customer_No_")
			.equals(String(clientId))
			.toArray();

		const mapById = new Map();
		[...byLocalId, ...bySellToNum, ...bySellToStr].forEach((inv) => {
			if (inv && !mapById.has(inv.id)) mapById.set(inv.id, inv);
		});
		const invoices = Array.from(mapById.values());

		// No hay facturas que actualizar
		if (invoices.length === 0) {
			return 0;
		}

		// Actualizar cada factura con el No del cliente
		for (const invoice of invoices) {
			await db.invoices.update(invoice.id, {
				Sell_to_Customer_No_: clientNo,
				clientLocalId: null, // Ya no necesitamos guardar el ID local
				lastModified: new Date(),
				pendingSync: 1,
			});

			// Marcar como pendiente de sincronización
			await markForSync("invoices", invoice.id);
		}

		return invoices.length;
	} catch (error) {
		console.error(`Error al actualizar No de cliente en facturas:`, error);
		throw error;
	}
};

export const getInvoicesByDateRange = async (
	start,
	end = '',
	turn,
	tpv,
	type = 'Invoice'
) => {

	if (!end) {
		end = start;
	}

	let invoices = await await db.invoices
		.where("Document_Type")
		.equals(type)
		.and(l => !l.deleted)
		.toArray();

	invoices = invoices.filter(x => {
		const date = new Date(x.Posting_Date);
		return date >= new Date(start) && date <= new Date(end);
	});

	//TODO: ADD FIELD TO INDEXES
	//"KeyPath Posting_Date on object store invoices is not indexed"
	// var invoices = await db.invoices
	// 	.where("Document_Date")
	// 	.between(start, end, true, true)
	// 	.toArray();

	return invoices.filter(inv =>
		(!turn || inv.turno === turn) &&
		(!tpv || inv.caja === tpv)
	);
}

export const getInvoicesLines = async (invoiceNos, type = "Invoice") => {

	const hasNumber = invoiceNos.some(x => typeof x === "number");
	let invoices = await db.invoiceLines
		.where(!hasNumber ? "invoiceNo" : "invoiceId")
		.anyOf(invoiceNos)
		.and(l => !l.deleted)
		.toArray();

	return invoices;
}
